// src/portal-app/operations/ReservationsScreen.js
//
// Plan 4 Task 7 — Reservations screen, migrated to the P4 pattern (spec §5).
// operations/TransactionsScreen.js:32-55 is the reference implementation;
// operations/ExpensesScreen.js (Task 6) is the closest reviewed model. This
// header records only what is specific to Reservations.
//
// FILE SPLIT (spec §7). This file was 1690 lines — the largest in the app —
// and was split first, in its own mechanical commit, before any P4 work:
//   reservationsShared.js  CSS + pure date/time helpers + tone maps
//   ReservationForms.js    every write path (guest search, create
//                          reservation, seat, add to waitlist)
//   ReservationRows.js     the read path (one reservation / one WL entry)
//   ReservationsScreen.js  state, fetching and layout only  (this file)
// The boundary is responsibility, not line count: the write path is where
// payload shape matters and must be reviewed against the API; the read path
// is where field mapping matters; this file is where fetch timing and
// control placement matter. They fail for different reasons.
//
// SEGMENTED CONTROL — VERIFIED as a genuine sibling-view switch, not a
// categorical filter in disguise (the defect a Task 4 review caught on
// Products' Active/Inactive toggle). Reservations hits
// reservationService.list({date, dining_option_id, status}) → GET
// /reservations/; Waiting List hits waitingListService.list({dining_option_id})
// → GET /waiting-list/. Different endpoints, different routers, different
// response models (ReservationResponse carries scheduled_for / table_number /
// duration_estimate / notes; WaitingListResponse carries position / joined_at /
// expected_wait_minutes and is server-filtered to statuses waiting+notified —
// backend/app/models/reservation_models.py:141,172 and
// backend/app/routers/waiting_list.py:55-73). Two independent SurfaceStates
// statuses, two independent seq guards. It stays OUT of FilterSheet.
//
// THREE FilterRows → ONE FilterSheet. Reservations carried a dining FilterRow
// AND a status FilterRow; Waiting List carried a third dining FilterRow. All
// three are now FilterSheet groups behind a single FilterButton — the sheet
// whose sticky header (Task 3C) exists because of this screen's option
// counts. Both are SERVER-side filters (query params on the list calls), so a
// filter matching nothing genuinely flips the surface to 'empty' — which is
// exactly why the hero, the date strip, the FilterButton and Clear all render
// OUTSIDE SurfaceStates. Inside, a filter that returned nothing would unmount
// the only controls able to undo it (the Plan 3 Task 2 bug).
//
// Also deleted with the FilterRows: a `display:none` block of duplicate
// `status-filter-*` buttons added "for test compatibility". Hidden
// interactive elements are a real a11y hazard (focusable, announced, not
// visible) and they existed only to paper over an untestable FilterRow mock.
// The FilterSheet groups are directly testable, so they are gone.
//
// HERO — covers, over exactly the rows rendered below it. Value is the sum of
// party_size across the CURRENT `reservations` array, so it moves with the
// selected date and with both filters: hero and rows always share one
// denominator (the rule Task 6's review had to enforce). Caption names the
// next upcoming BOOKED reservation from that same array — a cancelled or
// completed booking is not "upcoming" — or says so explicitly when there is
// none. The Waiting List tab gets its own hero on the same principle (covers
// waiting + longest current wait); the two never mix, exactly as Expenses'
// daily/overhead heroes are kept tab-scoped.
//
// NO SEARCH FIELD. The only SearchInput on this surface is inside
// GuestSearch, in the create/waitlist sheets — it searches the guest
// directory, not the list. Neither list has ever had a text search, both are
// short (one day of bookings; an active waiting list), and adding one would
// be a new behaviour, not a migration. The P4 recipe's "SearchInput renders
// outside SurfaceStates" rule has nothing to bind to here.
//
// FETCH TIMING IS UNCHANGED. Reservations still fetch on mount + on
// date/filter change; the waiting list still fetches lazily on first switch
// to that tab. No eager extra fetch was added to feed the heroes (the exact
// regression reverted in Task 6) — each hero is a reduction over the array
// its own tab has already fetched.
//
// SEQ GUARDS. fetchReservations kept its existing seqRef. fetchWaitingList
// had none — a slow first response could overwrite a faster second one after
// a filter flick. Added wlSeqRef, matching the reservations guard.
//
// Bug fixes carried over from W3 Task 5:
//   1. UTC bug: scheduled_for is built as `${date}T${time}:00`, a plain
//      local-time ISO string, NEVER via .toISOString() (which ships the wrong
//      business day for evening bookings outside UTC). Lives in
//      ReservationForms.CreateReservationForm.
//   2. res.time doesn't exist: display time is derived from scheduled_for
//      (reservationsShared.fmtScheduledFor).
//
// This screen renders CONTENT ONLY — NavStackHost owns the header.

import React, {
    useState, useEffect, useCallback, useMemo, useRef,
} from 'react';
import { useLanguage } from '../../context/LanguageContext';
import {
    reservationService,
    waitingListService,
    diningOptionService,
} from '../../services/api';
import { SurfaceStates } from '../insights/kit';
import {
    ConfirmSheet,
    SegmentedControl,
    FilterButton,
    FilterSheet,
    HeroStat,
    PullToRefresh,
    ensureKitCss,
} from '../shell/kit';
import { GlassCard } from '../shell/LiquidGlass';
import {
    ensureResCss,
    localDateStr,
    dateChipLabel,
    fmtScheduledFor,
    fmtWaitTime,
    parseLocal,
    RES_STATUSES,
} from './reservationsShared';
import {
    SeatSheet,
    CreateReservationForm,
    WaitlistForm,
} from './ReservationForms';
import { ReservationRow, WaitingListRow } from './ReservationRows';

/* ------------------------------------------------------------------ */
/* Inline SVG icons                                                    */
/* ------------------------------------------------------------------ */
const PlusIcon = () => (
    <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
    >
        <line x1="12" y1="5" x2="12" y2="19" />
        <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
);

/* ------------------------------------------------------------------ */
/* ReservationsScreen — main component                                 */
/* ------------------------------------------------------------------ */
export default function ReservationsScreen() {
    ensureResCss();
    ensureKitCss();
    const { t } = useLanguage();

    // ── Segment ──────────────────────────────────────────────────────────────
    const [segment, setSegment] = useState('reservations');

    // ── Shared data ──────────────────────────────────────────────────────────
    const [diningOptions, setDiningOptions] = useState([]);

    // ── Reservations tab state ───────────────────────────────────────────────
    const [selectedDate, setSelectedDate] = useState(localDateStr(0));
    const [diningFilter, setDiningFilter] = useState(null);
    const [statusFilter, setStatusFilter] = useState(null);
    const [reservations, setReservations] = useState([]);
    const [resStatus, setResStatus] = useState('loading');
    const seqRef = useRef(0);

    // ── Waiting list tab state ───────────────────────────────────────────────
    const [wlDiningFilter, setWlDiningFilter] = useState(null);
    const [waitingList, setWaitingList] = useState([]);
    const [wlStatus, setWlStatus] = useState('loading');
    const wlSeqRef = useRef(0);

    // ── Filter sheet ─────────────────────────────────────────────────────────
    const [filterSheetOpen, setFilterSheetOpen] = useState(false);

    // ── Seat sheet (shared by reservations + waiting list) ───────────────────
    const [seatTarget, setSeatTarget] = useState(null); // { type: 'res'|'wl', item }
    const [seatSheetOpen, setSeatSheetOpen] = useState(false);

    // ── Cancel / No Show confirm ─────────────────────────────────────────────
    const [confirmTarget, setConfirmTarget] = useState(null); // { action, item }
    const [confirmLoading, setConfirmLoading] = useState(false);

    // ── Create form ──────────────────────────────────────────────────────────
    const [createFormOpen, setCreateFormOpen] = useState(false);

    // ── Waitlist form ────────────────────────────────────────────────────────
    const [wlFormOpen, setWlFormOpen] = useState(false);

    // ── Date chips (today + 7 days forward) ─────────────────────────────────
    const dateChips = Array.from({ length: 8 }, (_, i) => ({
        date: localDateStr(i),
        ...dateChipLabel(i, t),
    }));

    // ── Fetch dining options ─────────────────────────────────────────────────
    useEffect(() => {
        diningOptionService.getAll()
            .then((opts) => setDiningOptions(opts || []))
            .catch(() => setDiningOptions([]));
    }, []);

    // ── Fetch reservations ───────────────────────────────────────────────────
    const fetchReservations = useCallback(async (date, diningId, status) => {
        const seq = ++seqRef.current;
        setResStatus('loading');
        try {
            const params = { date };
            if (diningId !== null && diningId !== undefined) params.dining_option_id = diningId;
            if (status) params.status = status;
            const data = await reservationService.list(params);
            if (seq !== seqRef.current) return;
            setReservations(data || []);
            setResStatus((data || []).length === 0 ? 'empty' : 'ready');
        } catch (_) {
            if (seq !== seqRef.current) return;
            setResStatus('error');
        }
    }, []);

    useEffect(() => {
        if (segment === 'reservations') {
            fetchReservations(selectedDate, diningFilter, statusFilter);
        }
    }, [segment, selectedDate, diningFilter, statusFilter, fetchReservations]);

    // ── Fetch waiting list ───────────────────────────────────────────────────
    const fetchWaitingList = useCallback(async (diningId) => {
        const seq = ++wlSeqRef.current;
        setWlStatus('loading');
        try {
            const params = {};
            if (diningId !== null && diningId !== undefined) params.dining_option_id = diningId;
            const data = await waitingListService.list(params);
            if (seq !== wlSeqRef.current) return;
            setWaitingList(data || []);
            setWlStatus((data || []).length === 0 ? 'empty' : 'ready');
        } catch (_) {
            if (seq !== wlSeqRef.current) return;
            setWlStatus('error');
        }
    }, []);

    useEffect(() => {
        if (segment === 'waiting_list') {
            fetchWaitingList(wlDiningFilter);
        }
    }, [segment, wlDiningFilter, fetchWaitingList]);

    // ── Handlers ─────────────────────────────────────────────────────────────

    const handleSeat = (item, type = 'res') => {
        setSeatTarget({ type, item });
        setSeatSheetOpen(true);
    };

    const handleSeatConfirm = async ({ table_number, dining_option_id }) => {
        if (!seatTarget) return;
        const { type, item } = seatTarget;
        if (type === 'res') {
            await reservationService.seat(item.id, { table_number, dining_option_id });
            setSeatSheetOpen(false);
            setSeatTarget(null);
            fetchReservations(selectedDate, diningFilter, statusFilter);
        } else {
            await waitingListService.seat(item.id, { table_number, dining_option_id });
            setSeatSheetOpen(false);
            setSeatTarget(null);
            fetchWaitingList(wlDiningFilter);
        }
    };

    const handleCancel = (res) => {
        setConfirmTarget({ action: 'cancel', item: res });
    };

    const handleNoShow = (res) => {
        setConfirmTarget({ action: 'noshow', item: res });
    };

    const handleConfirm = async () => {
        if (!confirmTarget) return;
        setConfirmLoading(true);
        const { action, item } = confirmTarget;
        try {
            if (action === 'cancel') {
                await reservationService.cancel(item.id);
            } else if (action === 'noshow') {
                await reservationService.noShow(item.id);
            }
            setConfirmTarget(null);
            setConfirmLoading(false);
            fetchReservations(selectedDate, diningFilter, statusFilter);
        } catch (_) {
            setConfirmLoading(false);
        }
    };

    const handleWlNotify = async (entry) => {
        try {
            await waitingListService.notify(entry.id);
            fetchWaitingList(wlDiningFilter);
        } catch (_) { /* ignore */ }
    };

    const handleWlLeft = async (entry) => {
        try {
            await waitingListService.leave(entry.id);
            fetchWaitingList(wlDiningFilter);
        } catch (_) { /* ignore */ }
    };

    const handleWlNoShow = async (entry) => {
        try {
            await waitingListService.noShow(entry.id);
            fetchWaitingList(wlDiningFilter);
        } catch (_) { /* ignore */ }
    };

    // ── Segment options ──────────────────────────────────────────────────────
    const segOptions = [
        { id: 'reservations', label: t('reservations', {}, 'Reservations') },
        { id: 'waiting_list', label: t('waiting_list', {}, 'Waiting List') },
    ];

    const isReservations = segment === 'reservations';

    // ── Hero: covers over exactly the rows rendered below ────────────────────
    const covers = useMemo(
        () => reservations.reduce((sum, r) => sum + (Number(r.party_size) || 0), 0),
        [reservations],
    );

    // Next upcoming = earliest still-future BOOKED reservation in the visible
    // list. Seated/completed/cancelled/no-show bookings are not "upcoming".
    const nextUpcoming = useMemo(() => {
        const now = Date.now();
        return reservations
            .filter((r) => r.status === 'booked')
            .map((r) => ({ res: r, at: parseLocal(r.scheduled_for) }))
            .filter((x) => x.at && x.at.getTime() >= now)
            .sort((a, b) => a.at.getTime() - b.at.getTime())[0] || null;
    }, [reservations]);

    const resHeroCaption = nextUpcoming
        ? t(
            'next_reservation_at',
            { time: fmtScheduledFor(nextUpcoming.res.scheduled_for) },
            'Next at {time}',
        )
        : t('no_upcoming_reservations', {}, 'No upcoming reservations');

    // ── Waiting list hero: covers waiting + longest current wait ─────────────
    const coversWaiting = useMemo(
        () => waitingList.reduce((sum, e) => sum + (Number(e.party_size) || 0), 0),
        [waitingList],
    );

    const longestWaitEntry = useMemo(() => {
        // Longest wait = earliest joined_at among the visible (active) entries.
        return waitingList
            .map((e) => ({ entry: e, at: parseLocal(e.joined_at) }))
            .filter((x) => x.at)
            .sort((a, b) => a.at.getTime() - b.at.getTime())[0] || null;
    }, [waitingList]);

    const wlHeroCaption = longestWaitEntry
        ? t(
            'longest_wait_of',
            { time: fmtWaitTime(longestWaitEntry.entry.joined_at, t) },
            'Longest wait {time}',
        )
        : undefined;

    // ── FilterSheet groups (three former FilterRows) ─────────────────────────
    const diningGroupOptions = useMemo(
        () => diningOptions.map((d) => ({ id: d.id, label: d.name })),
        [diningOptions],
    );

    const statusGroupOptions = useMemo(
        () => RES_STATUSES.map((s) => ({ id: s, label: t(s, {}, s.replace(/_/g, ' ')) })),
        [t],
    );

    const filterGroups = useMemo(() => {
        const groups = [];
        if (diningGroupOptions.length > 0) {
            groups.push({
                id: 'dining',
                label: t('dining_option', {}, 'Dining Option'),
                options: diningGroupOptions,
            });
        }
        // The waiting list has no status group: its endpoint only ever returns
        // active entries (waiting + notified), so there is nothing to filter.
        if (isReservations) {
            groups.push({
                id: 'status',
                label: t('reservation_status', {}, 'Status'),
                options: statusGroupOptions,
            });
        }
        return groups;
    }, [diningGroupOptions, statusGroupOptions, isReservations, t]);

    const filterValue = isReservations
        ? { dining: diningFilter, status: statusFilter }
        : { dining: wlDiningFilter };

    const activeFilters = isReservations
        ? [diningFilter, statusFilter]
        : [wlDiningFilter];
    const activeFilterCount = activeFilters.filter((v) => v != null).length;

    const diningLabel = (id) => {
        const opt = diningOptions.find((d) => d.id === id);
        return opt ? opt.name : String(id);
    };

    const activeFilterText = [
        (isReservations ? diningFilter : wlDiningFilter) != null
            ? diningLabel(isReservations ? diningFilter : wlDiningFilter)
            : null,
        isReservations && statusFilter
            ? t(statusFilter, {}, statusFilter.replace(/_/g, ' '))
            : null,
    ].filter(Boolean).join(' · ');

    const handleFilterChange = (groupId, optionId) => {
        if (!isReservations) {
            if (groupId === 'dining') setWlDiningFilter(optionId);
            return;
        }
        if (groupId === 'dining') setDiningFilter(optionId);
        else if (groupId === 'status') setStatusFilter(optionId);
    };

    // Clears the ACTIVE tab's filters only — the two tabs keep independent
    // filter state, exactly as they did before this migration.
    const clearAllFilters = () => {
        if (isReservations) {
            setDiningFilter(null);
            setStatusFilter(null);
        } else {
            setWlDiningFilter(null);
        }
    };

    // ── Confirm sheet labels ─────────────────────────────────────────────────
    const confirmTitle = confirmTarget?.action === 'cancel'
        ? t('cancel_reservation', {}, 'Cancel Reservation')
        : t('mark_no_show', {}, 'Mark No Show');
    const confirmMsg = confirmTarget?.action === 'cancel'
        ? t('cancel_reservation_msg', {}, 'This reservation will be cancelled.')
        : t('mark_no_show_msg', {}, 'Mark this guest as a no-show?');

    // Pull-to-refresh is suppressed whenever a sheet or overlay owns the
    // screen, matching Inventory/Expenses.
    const ptrDisabled = filterSheetOpen
        || seatSheetOpen
        || !!confirmTarget
        || createFormOpen
        || wlFormOpen;

    // ── Shared control row pieces ────────────────────────────────────────────
    const filterSummary = activeFilterCount > 0 ? (
        <div className="res-filtersum">
            <span className="res-filtersum-text">{activeFilterText}</span>
            <button
                type="button"
                className="res-filtersum-clear"
                data-testid="clear-filters"
                onClick={clearAllFilters}
            >
                {t('clear', {}, 'Clear')}
            </button>
        </div>
    ) : null;

    return (
        <div className="res-wrap">
            {/* ── Segment control — two different endpoints, two different
                   response models (see file header). OUTSIDE SurfaceStates. ─── */}
            <div className="res-seg">
                <SegmentedControl
                    options={segOptions}
                    value={segment}
                    onChange={setSegment}
                />
            </div>

            {/* ── RESERVATIONS TAB ─── */}
            {isReservations && (
                <>
                    {/* Hero — covers across exactly the rows below. OUTSIDE
                        SurfaceStates so it never vanishes with the list. */}
                    <GlassCard veil className="res-hero" data-testid="reservations-hero">
                        <HeroStat
                            label={t('covers', {}, 'Covers')}
                            value={covers}
                            caption={resHeroCaption}
                        />
                    </GlassCard>

                    {/* One control row: date strip leading, FilterButton
                        trailing. Both OUTSIDE SurfaceStates — a filter that
                        returns nothing must not unmount the means to undo it. */}
                    <div className="res-control-row">
                        <div
                            className="res-date-strip"
                            role="group"
                            aria-label={t('select_date', {}, 'Select date')}
                        >
                            {dateChips.map((chip) => (
                                <button
                                    key={chip.date}
                                    type="button"
                                    className={`res-date-chip${chip.date === selectedDate ? ' res-date-chip-active' : ''}`}
                                    data-testid={`date-chip-${chip.date}`}
                                    aria-pressed={chip.date === selectedDate}
                                    onClick={() => setSelectedDate(chip.date)}
                                >
                                    <span className="res-date-chip-day">{chip.day}</span>
                                    <span className="res-date-chip-num">{chip.num}</span>
                                </button>
                            ))}
                        </div>
                        <FilterButton
                            activeCount={activeFilterCount}
                            onPress={() => setFilterSheetOpen(true)}
                        />
                    </div>

                    {filterSummary}

                    <PullToRefresh
                        onRefresh={() => fetchReservations(selectedDate, diningFilter, statusFilter)}
                        disabled={ptrDisabled}
                    >
                        <SurfaceStates
                            status={resStatus}
                            onRetry={() => fetchReservations(selectedDate, diningFilter, statusFilter)}
                            emptyTitle={t('no_reservations', {}, 'No reservations')}
                            emptyMsg={t('no_reservations_msg', {}, 'No reservations found for this date.')}
                        >
                            <div className="res-count-line" data-testid="reservations-count-line">
                                {t('n_reservations', { count: reservations.length }, '{count} Reservations')}
                            </div>

                            <div className="res-list">
                                {reservations.map((res) => (
                                    <ReservationRow
                                        key={res.id}
                                        res={res}
                                        diningOptions={diningOptions}
                                        onSeat={(r) => handleSeat(r, 'res')}
                                        onCancel={handleCancel}
                                        onNoShow={handleNoShow}
                                        t={t}
                                    />
                                ))}
                            </div>
                        </SurfaceStates>
                    </PullToRefresh>

                    {/* FAB — Add Reservation */}
                    <button
                        type="button"
                        className="res-fab"
                        data-testid="fab-add-reservation"
                        aria-label={t('add_reservation', {}, 'Add Reservation')}
                        onClick={() => setCreateFormOpen(true)}
                    >
                        <PlusIcon />
                        {t('add_reservation', {}, 'Add Reservation')}
                    </button>
                </>
            )}

            {/* ── WAITING LIST TAB ─── */}
            {!isReservations && (
                <>
                    {/* Hero — this tab's own list only; never combined with
                        the reservations tab (different fetch, different day
                        window, no shared denominator). */}
                    <GlassCard veil className="res-hero" data-testid="waitlist-hero">
                        <HeroStat
                            label={t('covers_waiting', {}, 'Covers Waiting')}
                            value={coversWaiting}
                            caption={wlHeroCaption}
                        />
                    </GlassCard>

                    {/* Control row — FilterButton only; the waiting list has
                        no date dimension (it is always "now"). */}
                    <div className="res-control-row res-control-row-end">
                        <FilterButton
                            activeCount={activeFilterCount}
                            onPress={() => setFilterSheetOpen(true)}
                        />
                    </div>

                    {filterSummary}

                    <PullToRefresh
                        onRefresh={() => fetchWaitingList(wlDiningFilter)}
                        disabled={ptrDisabled}
                    >
                        <SurfaceStates
                            status={wlStatus}
                            onRetry={() => fetchWaitingList(wlDiningFilter)}
                            emptyTitle={t('no_waitlist', {}, 'No one waiting')}
                            emptyMsg={t('no_waitlist_msg', {}, 'The waiting list is empty.')}
                        >
                            <div className="res-count-line" data-testid="waitlist-count-line">
                                {t('n_waiting', { count: waitingList.length }, '{count} Waiting')}
                            </div>

                            <div className="res-list">
                                {waitingList.map((entry) => (
                                    <WaitingListRow
                                        key={entry.id}
                                        entry={entry}
                                        onNotify={handleWlNotify}
                                        onSeat={(e) => handleSeat(e, 'wl')}
                                        onLeft={handleWlLeft}
                                        onNoShow={handleWlNoShow}
                                        t={t}
                                    />
                                ))}
                            </div>
                        </SurfaceStates>
                    </PullToRefresh>

                    {/* FAB — Add to Waitlist. Was an inline button above the
                        filters; promoted to a FAB so both tabs put their one
                        create action in the same place and the control rows
                        stay within the P4 maximum. Same handler, same testid. */}
                    <button
                        type="button"
                        className="res-fab"
                        data-testid="add-to-waitlist-btn"
                        aria-label={t('add_to_waitlist', {}, 'Add to Waitlist')}
                        onClick={() => setWlFormOpen(true)}
                    >
                        <PlusIcon />
                        {t('add_to_waitlist', {}, 'Add to Waitlist')}
                    </button>
                </>
            )}

            {/* ── Seat sheet ─── */}
            <SeatSheet
                open={seatSheetOpen}
                onClose={() => { setSeatSheetOpen(false); setSeatTarget(null); }}
                onConfirm={handleSeatConfirm}
                diningOptions={diningOptions}
                initialDiningId={seatTarget?.item?.dining_option_id}
                t={t}
            />

            {/* ── Cancel / No Show ConfirmSheet ─── */}
            <ConfirmSheet
                open={!!confirmTarget}
                onClose={() => { setConfirmTarget(null); setConfirmLoading(false); }}
                onConfirm={handleConfirm}
                title={confirmTitle}
                message={confirmMsg}
                destructive
                loading={confirmLoading}
                confirmLabel={confirmTarget?.action === 'cancel'
                    ? t('cancel_reservation', {}, 'Cancel')
                    : t('mark_no_show', {}, 'No Show')}
            />

            {/* ── Create reservation form ─── */}
            <CreateReservationForm
                open={createFormOpen}
                onClose={() => setCreateFormOpen(false)}
                onSaved={() => {
                    setCreateFormOpen(false);
                    fetchReservations(selectedDate, diningFilter, statusFilter);
                }}
                diningOptions={diningOptions}
                initialDate={selectedDate}
                t={t}
            />

            {/* ── Waitlist form ─── */}
            <WaitlistForm
                open={wlFormOpen}
                onClose={() => setWlFormOpen(false)}
                onSaved={() => {
                    setWlFormOpen(false);
                    fetchWaitingList(wlDiningFilter);
                }}
                diningOptions={diningOptions}
                t={t}
            />

            {/* ── Filter sheet — the three former FilterRows ─── */}
            <FilterSheet
                open={filterSheetOpen}
                onClose={() => setFilterSheetOpen(false)}
                groups={filterGroups}
                value={filterValue}
                onChange={handleFilterChange}
                onClear={clearAllFilters}
            />
        </div>
    );
}
